import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Save, X, ArrowLeft, Upload, Trash } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { surahAPI, verseAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const VerseManagement = () => {
  const { surahId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [surah, setSurah] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVerse, setEditingVerse] = useState(null);
  const [selectedVerses, setSelectedVerses] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    verseNumber: 1,
    textArabic: '',
    textTurkish: '',
    translation: '',
    order: 1
  });

  useEffect(() => {
    loadData();
  }, [surahId]);

  const loadData = async () => {
    try {
      const [surahRes, versesRes] = await Promise.all([
        surahAPI.getOne(surahId),
        verseAPI.getBySurah(surahId)
      ]);
      setSurah(surahRes.data);
      setVerses(versesRes.data);
      setSelectedVerses([]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Hata!',
        description: 'Veriler yuklenirken hata olustu.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVerse = (verseId) => {
    setSelectedVerses(prev => 
      prev.includes(verseId)
        ? prev.filter(id => id !== verseId)
        : [...prev, verseId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVerses.length === verses.length) {
      setSelectedVerses([]);
    } else {
      setSelectedVerses(verses.map(v => v.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await verseAPI.bulkDelete(selectedVerses);
      toast({
        title: 'Basarili!',
        description: `${selectedVerses.length} ayet basariyla silindi.`
      });
      setIsDeleteModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error bulk deleting verses:', error);
      toast({
        title: 'Hata!',
        description: 'Ayetler silinirken hata olustu.',
        variant: 'destructive'
      });
    }
  };

  const handleOpenDialog = (verse = null) => {
    if (verse) {
      setEditingVerse(verse);
      setFormData({
        verseNumber: verse.verseNumber,
        textArabic: verse.textArabic,
        textTurkish: verse.textTurkish,
        translation: verse.translation,
        order: verse.order
      });
    } else {
      setEditingVerse(null);
      setFormData({
        verseNumber: verses.length + 1,
        textArabic: '',
        textTurkish: '',
        translation: '',
        order: verses.length + 1
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingVerse(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['order'].includes(name) ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        surahId: surahId
      };

      if (editingVerse) {
        await verseAPI.update(editingVerse.id, data);
        toast({
          title: 'Basarili!',
          description: 'Ayet basariyla guncellendi.'
        });
      } else {
        await verseAPI.create(data);
        toast({
          title: 'Basarili!',
          description: 'Ayet basariyla eklendi.'
        });
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving verse:', error);
      toast({
        title: 'Hata!',
        description: error.response?.data?.detail || 'Ayet kaydedilirken hata olustu.',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ayeti silmek istediginizden emin misiniz?')) return;
    
    try {
      await verseAPI.delete(id);
      toast({
        title: 'Basarili!',
        description: 'Ayet basariyla silindi.'
      });
      loadData();
    } catch (error) {
      console.error('Error deleting verse:', error);
      toast({
        title: 'Hata!',
        description: 'Ayet silinirken hata olustu.',
        variant: 'destructive'
      });
    }
  };

  if (loading || !surah) {
    return <div className="flex items-center justify-center h-64"><p className="text-[#8B7355]">Yukleniyor...</p></div>;
  }

  return (
    <div>
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/surahs')}
          className="mb-4 text-[#6B5744] hover:bg-[#E8DCC8]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Surelere Don
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#5C4033]">{surah.name} - Ayet Yonetimi</h2>
            <p className="text-[#8B7355]">{surah.nameArabic} • {verses.length} Ayet</p>
          </div>
          <div className="flex space-x-2">
            {selectedVerses.length > 0 && (
              <Button
                onClick={() => setIsDeleteModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash className="h-4 w-4 mr-2" />
                Seçilenleri Sil ({selectedVerses.length})
              </Button>
            )}
            <Button
              onClick={() => navigate(`/admin/surahs/${surahId}/bulk-upload`)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Upload className="h-4 w-4 mr-2" />
              Toplu Ekle
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tek Ayet Ekle
            </Button>
          </div>
        </div>
      </div>

      {/* Select All */}
      {verses.length > 0 && (
        <div className="mb-4">
          <label className="flex items-center space-x-2 text-sm text-[#6B5744] cursor-pointer">
            <input
              type="checkbox"
              checked={selectedVerses.length === verses.length}
              onChange={handleSelectAll}
              className="w-4 h-4 text-[#8B7355] border-[#D5C4A1] rounded focus:ring-[#8B7355]"
            />
            <span>Tümünü Seç / Temizle</span>
          </label>
        </div>
      )}

      <div className="space-y-3">
        {verses.map((verse) => (
          <Card key={verse.id} className="bg-white border-[#E8DCC8] p-4">
            <div className="flex items-start space-x-4">
              {/* Checkbox */}
              <div className="flex-shrink-0 pt-2">
                <input
                  type="checkbox"
                  checked={selectedVerses.includes(verse.id)}
                  onChange={() => handleSelectVerse(verse.id)}
                  className="w-5 h-5 text-[#8B7355] border-[#D5C4A1] rounded focus:ring-[#8B7355]"
                />
              </div>

              <div className="flex-shrink-0 w-10 h-10 bg-[#D5C4A1] rounded-full flex items-center justify-center text-[#5C4033] font-bold mt-1">
                {verse.verseNumber}
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <p className="text-xs text-[#8B7355] mb-1">Arapca:</p>
                  <p className="text-xl text-[#3E3028] text-right leading-loose" style={{ fontFamily: '"Traditional Arabic", serif' }}>
                    {verse.textArabic}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-[#8B7355] mb-1">Turkce:</p>
                  <p className="text-base text-[#3E3028] leading-relaxed">
                    {verse.textTurkish}
                  </p>
                </div>
              </div>

              <div className="flex-shrink-0 flex space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(verse)}
                  className="text-[#8B7355] hover:bg-[#F5EFE7]"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(verse.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Edit/Add Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingVerse ? 'Ayet Duzenle' : 'Yeni Ayet Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Ayet Numarasi</label>
                <Input
                  name="verseNumber"
                  value={formData.verseNumber}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sira</label>
                <Input
                  name="order"
                  type="number"
                  value={formData.order}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Arapca Metin</label>
              <Textarea
                name="textArabic"
                value={formData.textArabic}
                onChange={handleChange}
                rows={4}
                required
                className="text-right"
                style={{ fontFamily: '"Traditional Arabic", serif' }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Turkce Metin</label>
              <Textarea
                name="textTurkish"
                value={formData.textTurkish}
                onChange={handleChange}
                rows={4}
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Tercume</label>
              <Textarea
                name="translation"
                value={formData.translation}
                onChange={handleChange}
                rows={4}
                required
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="ghost" onClick={handleCloseDialog}>
                <X className="h-4 w-4 mr-2" />
                Iptal
              </Button>
              <Button type="submit" className="bg-[#8B7355] hover:bg-[#6B5744] text-white">
                <Save className="h-4 w-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Toplu Silme Onayı</DialogTitle>
            <DialogDescription>
              <p className="text-base mt-2">
                <strong>{selectedVerses.length} adet</strong> ayeti silmek üzeresiniz.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Bu işlem geri alınamaz! Devam etmek istediğinizden emin misiniz?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex space-x-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              İptal
            </Button>
            <Button
              type="button"
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash className="h-4 w-4 mr-2" />
              Evet, Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerseManagement;
